'use client';

/**
 * Landing Filter Pills
 *
 * The "Find your best AI girl" filter selector — three glowing dropdown pills
 * (Style / Gender / Age) matching the marketing design. Wires directly into
 * the shared ExploreFilter context so selections drive the character grid,
 * exactly like the previous inline chips did.
 */
import { type CSSProperties, useState } from 'react';

import Link from 'next/link';

import { Calendar, ChevronDown, ChevronRight, Sparkles } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { useExploreFiltersOptional } from '~/components/navigation/explore-filter-context';

const STYLE_OPTIONS = [
  { label: 'Realistic', i18n: 'common:filters.realistic' },
  { label: 'Anime', i18n: 'common:filters.anime' },
  { label: 'Furry', i18n: 'common:filters.furry' },
];

const GENDER_OPTIONS = [
  { label: 'Female', i18n: 'common:filters.female' },
  { label: 'Male', i18n: 'common:filters.male' },
  { label: 'Trans', i18n: 'common:filters.trans' },
];

const AGE_OPTIONS = ['18-21', '22-30', '30-35', '35+'] as const;

type OpenMenu = 'style' | 'gender' | 'age' | null;

/** Venus / Mars / Transgender glyph for the gender pill. */
function GenderGlyph({
  gender,
  className,
}: {
  gender: string;
  className?: string;
}) {
  const common = {
    viewBox: '0 0 24 24',
    fill: 'none',
    stroke: 'currentColor',
    strokeWidth: 2.2,
    strokeLinecap: 'round' as const,
    strokeLinejoin: 'round' as const,
    className,
  };
  if (gender === 'Male') {
    return (
      <svg {...common}>
        <circle cx="10" cy="14" r="6" />
        <line x1="22" y1="2" x2="15" y2="9" />
        <polyline points="16 2 22 2 22 8" />
      </svg>
    );
  }
  if (gender === 'Trans') {
    return (
      <svg {...common}>
        <circle cx="11" cy="12" r="5" />
        <line x1="11" y1="17" x2="11" y2="22" />
        <line x1="8" y1="20" x2="14" y2="20" />
        <line x1="19" y1="2" x2="13.5" y2="7.5" />
        <polyline points="14 2 19 2 19 7" />
      </svg>
    );
  }
  return (
    <svg {...common}>
      <circle cx="12" cy="9" r="6" />
      <line x1="12" y1="15" x2="12" y2="22" />
      <line x1="9" y1="19" x2="15" y2="19" />
    </svg>
  );
}

export function LandingFilterPills() {
  const { t } = useTranslation();
  const filters = useExploreFiltersOptional();
  const [open, setOpen] = useState<OpenMenu>(null);

  const currentStyle =
    STYLE_OPTIONS.find((o) => filters?.isFilterSelected(o.label))?.label ??
    'Realistic';
  // null when no gender filter is applied. It used to default to 'Female',
  // which labelled an UNFILTERED catalogue as if it were women-only — the same
  // assumption that kept male and trans characters off the homepage.
  const currentGender =
    GENDER_OPTIONS.find((o) => filters?.isFilterSelected(o.label))?.label ??
    null;
  const currentAge =
    AGE_OPTIONS.find((a) => filters?.isFilterSelected(a)) ?? null;

  // Make a group mutually exclusive: deselect every other member, then select
  // `label` (or clear the group entirely when label is null).
  const setExclusive = (group: readonly string[], label: string | null) => {
    if (!filters) return;
    for (const g of group) {
      if (g !== label && filters.isFilterSelected(g)) filters.toggleFilter(g);
    }
    if (label && !filters.isFilterSelected(label)) filters.toggleFilter(label);
  };

  const pillBase =
    'relative flex w-full flex-shrink-0 items-center justify-center rounded-full font-semibold transition-all sm:w-auto sm:justify-start';
  // Fluid sizing — buttons scale smoothly with the viewport (no breakpoint
  // jumps), so they stay proportionate on laptops and shrink gracefully on
  // smaller screens.
  const pillSize: CSSProperties = {
    gap: 'clamp(5px, 0.55vw, 9px)',
    padding: 'clamp(7px, 0.65vw, 11px) clamp(9px, 1.25vw, 22px)',
    fontSize: 'clamp(12px, 0.95vw, 16px)',
  };
  const pillActive =
    'from-eros-pink to-eros-purple bg-gradient-to-r text-white shadow-[0_0_22px_rgba(236,72,153,0.45)]';
  const pillIdle =
    'border border-white/10 bg-white/5 text-white hover:border-white/20 hover:bg-white/10';

  const menuClass =
    'absolute left-0 top-full z-50 mt-2 overflow-hidden rounded-2xl border border-white/10 p-1.5 shadow-2xl';
  // Menus stay mounted and reveal via inline opacity + transform (not a CSS
  // keyframe), so the animation is smooth regardless of CSS recompilation.
  // Solid background + min-width set inline so neither depends on Tailwind JIT.
  const menuStyleFor = (isOpen: boolean): CSSProperties => ({
    backgroundColor: '#1a1330',
    minWidth: '170px',
    maxWidth: 'calc(100vw - 1.5rem)',
    opacity: isOpen ? 1 : 0,
    transform: isOpen
      ? 'translateY(0) scale(1)'
      : 'translateY(-8px) scale(0.96)',
    transformOrigin: 'top center',
    pointerEvents: isOpen ? 'auto' : 'none',
    transition:
      'opacity 0.22s ease, transform 0.22s cubic-bezier(0.16, 1, 0.3, 1)',
  });
  const optClass = (selected: boolean) =>
    `flex w-full items-center rounded-xl px-3 py-2 text-left text-sm transition-colors ${
      selected
        ? 'bg-white/10 text-white'
        : 'text-gray-300 hover:bg-white/5 hover:text-white'
    }`;

  return (
    <div className="relative flex flex-wrap gap-2.5 sm:gap-3">
      {/* Click-away backdrop (pills sit above it at z-50) */}
      {open && (
        <div
          className="fixed inset-0 z-40"
          aria-hidden
          onClick={() => setOpen(null)}
        />
      )}

      {/* Style */}
      <div className="relative z-50 min-w-0 flex-1 sm:flex-none">
        <button
          type="button"
          onClick={() => setOpen((o) => (o === 'style' ? null : 'style'))}
          className={`${pillBase} ${pillActive}`}
          style={pillSize}
        >
          <Sparkles className="h-4 w-4 sm:h-5 sm:w-5" />
          {t(
            STYLE_OPTIONS.find((o) => o.label === currentStyle)?.i18n ??
              'common:filters.realistic',
          )}
          <ChevronDown
            className={`hidden h-4 w-4 transition-transform sm:block ${open === 'style' ? 'rotate-180' : ''}`}
          />
        </button>
        <div
          className={menuClass}
          style={menuStyleFor(open === 'style')}
          aria-hidden={open !== 'style'}
        >
          {STYLE_OPTIONS.map((o) => (
            <button
              key={o.label}
              type="button"
              onClick={() => {
                setExclusive(
                  STYLE_OPTIONS.map((s) => s.label),
                  o.label,
                );
                setOpen(null);
              }}
              className={optClass(o.label === currentStyle)}
            >
              {t(o.i18n)}
            </button>
          ))}
        </div>
      </div>

      {/* Gender */}
      <div className="relative z-50 min-w-0 flex-1 sm:flex-none">
        <button
          type="button"
          onClick={() => setOpen((o) => (o === 'gender' ? null : 'gender'))}
          className={`${pillBase} ${pillIdle}`}
          style={pillSize}
        >
          <GenderGlyph
            gender={currentGender ?? 'Female'}
            className="text-eros-pink h-4 w-4 sm:h-5 sm:w-5"
          />
          {t(
            GENDER_OPTIONS.find((o) => o.label === currentGender)?.i18n ??
              'common:filters.anyGender',
          )}
          <ChevronDown
            className={`hidden h-4 w-4 transition-transform sm:block ${open === 'gender' ? 'rotate-180' : ''}`}
          />
        </button>
        <div
          className={menuClass}
          style={menuStyleFor(open === 'gender')}
          aria-hidden={open !== 'gender'}
        >
          {GENDER_OPTIONS.map((o) => (
            <button
              key={o.label}
              type="button"
              onClick={() => {
                setExclusive(
                  GENDER_OPTIONS.map((g) => g.label),
                  o.label,
                );
                setOpen(null);
              }}
              className={optClass(o.label === currentGender)}
            >
              {t(o.i18n)}
            </button>
          ))}
        </div>
      </div>

      {/* Age */}
      <div className="relative z-50 min-w-0 flex-1 sm:flex-none">
        <button
          type="button"
          onClick={() => setOpen((o) => (o === 'age' ? null : 'age'))}
          className={`${pillBase} ${currentAge ? pillActive : pillIdle}`}
          style={pillSize}
        >
          <Calendar
            className={`h-4 w-4 sm:h-5 sm:w-5 ${currentAge ? 'text-white' : 'text-eros-purple'}`}
          />
          {currentAge ?? 'Age'}
          <ChevronDown
            className={`hidden h-4 w-4 transition-transform sm:block ${open === 'age' ? 'rotate-180' : ''}`}
          />
        </button>
        <div
          className={menuClass}
          style={{ ...menuStyleFor(open === 'age'), left: 'auto', right: 0 }}
          aria-hidden={open !== 'age'}
        >
          <button
            type="button"
            onClick={() => {
              setExclusive(AGE_OPTIONS, null);
              setOpen(null);
            }}
            className={optClass(!currentAge)}
          >
            Any age
          </button>
          {AGE_OPTIONS.map((a) => (
            <button
              key={a}
              type="button"
              onClick={() => {
                setExclusive(AGE_OPTIONS, a);
                setOpen(null);
              }}
              className={optClass(a === currentAge)}
            >
              {a}
            </button>
          ))}
        </div>
      </div>

      <Link
        href="/catalog"
        style={pillSize}
        // The text is CSS-hidden on mobile (chevron only), so the label keeps
        // the link discernible to screen readers / AI agents (PSI flag).
        aria-label={t('marketing:landing.viewAllShort')}
        className="hover:border-eros-pink/50 inline-flex flex-shrink-0 items-center justify-center self-center rounded-full border border-white/10 bg-white/5 font-semibold text-white transition-colors hover:bg-white/10 sm:ml-auto"
      >
        <span className="hidden sm:inline">
          {t('marketing:landing.viewAllShort')}
        </span>
        <ChevronRight className="h-4 w-4 sm:h-5 sm:w-5" aria-hidden="true" />
      </Link>
    </div>
  );
}
